import {
  Listbox,
  Transition,
  Label,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import type { ValidationError } from '@tanstack/react-form';
import { Fragment, useMemo } from 'react';
import { IconType } from '@src/types/IconType';
import { classNames } from '@utils/classNames';
import { Icon } from '@components/atoms/Icon';

export type DropdownProps<Option> = {
  options: readonly Option[];
  handleChange: (newValue: Option) => void;
  displayValue: (value: Option) => string;
  value: Option;
  label: string;
  classNameContainer?: string;
  classNameInput?: string;
  disabled?: boolean;
  placeholder?: string;
  errors?: ValidationError[];
  touched?: boolean;
  testId?: string;
};

export const Dropdown = <T,>({
  value,
  displayValue,
  handleChange,
  label,
  classNameInput,
  classNameContainer,
  options,
  disabled = false,
  placeholder,
  errors,
  touched,
  testId,
}: DropdownProps<T>) => {
  const displayedValue = useMemo(() => {
    return displayValue(value);
  }, [value, displayValue]);

  const relevantErrors =
    errors?.filter((error): error is string => !!error) ?? [];

  return (
    <div className={classNameContainer}>
      <Listbox
        value={value}
        onChange={(value) => {
          handleChange(value);
        }}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Label
              className={classNames(
                'w-full inline-block mb-2 font-bold',
                disabled ? 'text-grey1' : 'text-dark',
              )}
              data-testid={`${testId}-select-label`}
            >
              {label}
            </Label>
            <div className={classNames('relative', classNameInput)}>
              <ListboxButton
                className={classNames(
                  'font-secondary px-3.5 py-2.5 border-solid w-full text-left rounded-bonusLarge h-10',
                  disabled ? '!bg-grey3light-50' : 'bg-grey3light',
                  !disabled && relevantErrors.length && touched
                    ? 'border-2 border-error'
                    : '',
                  classNameInput,
                )}
                data-testid={`${testId}-select-btn`}
              >
                <span
                  className={classNames(
                    'block truncate leading-snug text-sm font-light',
                    displayedValue ? 'text-dark' : 'text-grey1',
                  )}
                >
                  {displayedValue ? displayedValue : placeholder}
                  {/* This zero-width-space prevents the height from collapsing if the value is empty */}
                  &#8203;
                </span>
                {!disabled && (
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-5">
                    <Icon
                      type={IconType.ChevronDown}
                      className="h-3.5 w-3.5 text-grey1"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </ListboxButton>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto py-1 text-base bg-grey3light rounded-bonusLarge">
                  {options.map((option, index) => (
                    <ListboxOption
                      key={`${displayValue(option)}-${index}`}
                      data-testid={`${testId}-selectOption_${displayValue(option)}`}
                      className="relative cursor-default select-none py-2 pl-3 pr-9 hover:bg-grey2 rounded-bonusLarge text-sm"
                      value={option}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={classNames(
                              selected ? 'font-normal' : 'font-light',
                              'block truncate min-h-[22px]',
                            )}
                          >
                            {displayValue(option)}
                          </span>

                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                              <Icon
                                type={IconType.Checkmark}
                                className="h-5 w-5 text-grey1"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {!disabled &&
        touched &&
        !!relevantErrors.length &&
        relevantErrors.map((error) => {
          return (
            <p className="text-error" key={error.toString()}>
              {error}
            </p>
          );
        })}
    </div>
  );
};
